import React, { useEffect, useLayoutEffect, useRef, useState, ReactNode } from "react";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";

interface Props {
  // extra class on the scroll track, e.g. "v2-offers__track"
  trackClassName?: string;
  // pixels to scroll per arrow click (roughly one card width + gap)
  step?: number;
  children: ReactNode;
}

// Fixed-width card row in a native scroll-snap track. Native overflow scrolling
// clamps cleanly at both ends — no trailing empty space, unlike react-slick's
// variableWidth (whose track/paging math is derived from slidesToShow and leaves
// a gap at the end). Arrows scroll by ~one card and hide at the start/end.
export default function ScrollCarousel({ trackClassName = "", step = 360, children }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  // measure once synchronously so the arrows reflect the initial overflow state
  useLayoutEffect(update, []);

  useEffect(() => {
    const el = trackRef.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollByStep = (dir: number) => {
    trackRef.current?.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div className="v2-carousel">
      <button
        type="button"
        className={`v2-carousel-arrow v2-carousel-arrow--prev ${atStart ? "is-hidden" : ""}`}
        onClick={() => scrollByStep(-1)}
        aria-label="Previous"
      >
        <Icon path={mdiChevronLeft} size="24px" />
      </button>

      <div className={`v2-carousel__track ${trackClassName}`.trim()} ref={trackRef}>
        {children}
      </div>

      <button
        type="button"
        className={`v2-carousel-arrow v2-carousel-arrow--next ${atEnd ? "is-hidden" : ""}`}
        onClick={() => scrollByStep(1)}
        aria-label="Next"
      >
        <Icon path={mdiChevronRight} size="24px" />
      </button>
    </div>
  );
}
